import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import apiRequest from "../../../utils/vendor/apiRequest";
import InputField from "../../ui/form/InputField";
import FileInputField from "../../ui/form/FileInputField";
import Button from "../../ui/utils/Button";
import usePost from "../../../hooks/api/usePost";
import Alert from "../../ui/utils/Alert";

const EditVendorCompliance = ({ data, closeModal, callback }) => {
  const [preview, setPreview] = useState(null);

  const {
    error,
    isLoading: loading,
    post,
    serverErrors,
  } = usePost(apiRequest.updateVendorCompliance);

  const formik = useFormik({
    initialValues: {
      vendorName: data?.vendorName || "",
      location: data?.location || "",
      corporateCode: data?.corporateCode || "",
      licenseName: data?.licenseName || "",
      expiryDate: data?.expiryDate?.slice(0, 10) || "",
      notes: data?.notes || "",
      document: "",
    },

    validationSchema: Yup.object({
      vendorName: Yup.string().required("Vendor name is required"),
      location: Yup.string().required("Location is required"),
      corporateCode: Yup.string().required("Corporate code is required"),
      licenseName: Yup.string().required("License name is required"),
      expiryDate: Yup.string().required("Expiry date is required"),

      document: Yup.mixed().test(
        "fileType",
        "Invalid file type",
        (value) =>
          !value ||
          ["image/jpeg", "image/png", "application/pdf"].includes(value.type)
      ),
    }),

    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();

      formData.append("vendorName", values.vendorName);
      formData.append("location", values.location);
      formData.append("corporateCode", values.corporateCode);
      formData.append("licenseName", values.licenseName);
      formData.append("expiryDate", values.expiryDate);
      formData.append("notes", values.notes);

      if (values.document) {
        formData.append("document", values.document);
      }

      const response = await post({
        data: { formData, id: data?._id },
      });

      if (response?.response) {
        callback?.();
        resetForm();
        closeModal();
      }
    },
  });

  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Update Compliance</h2>
      <div className="border-b" />

      <form onSubmit={formik.handleSubmit} className="text-sm">
        {error && <Alert message={error} type="error" />}

        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">

          <InputField
            name="vendorName"
            label="Vendor Name"
            value={formik.values.vendorName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.vendorName && formik.errors.vendorName}
            serverError={serverErrors?.vendorName}
          />

          <InputField
            name="location"
            label="Location"
            value={formik.values.location}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.location && formik.errors.location}
            serverError={serverErrors?.location}
          />

          <InputField
            name="corporateCode"
            label="Corporate Code"
            value={formik.values.corporateCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.corporateCode && formik.errors.corporateCode}
            serverError={serverErrors?.corporateCode}
          />

          <InputField
            name="licenseName"
            label="License Name"
            value={formik.values.licenseName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.licenseName && formik.errors.licenseName}
            serverError={serverErrors?.licenseName}
          />

          <InputField
            name="expiryDate"
            type="date"
            label="Expiry Date"
            value={formik.values.expiryDate}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.expiryDate && formik.errors.expiryDate}
            serverError={serverErrors?.expiryDate}
          />

          <InputField
            name="notes"
            label="Notes"
            textarea
            value={formik.values.notes}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.notes && formik.errors.notes}
            serverError={serverErrors?.notes}
          />

          <div className="col-span-full">

            <FileInputField
              id="document"
              label="Document"
              accept="image/*,application/pdf"
              error={formik.touched.document && formik.errors.document}
              onChange={(e) => {
                const file = e.currentTarget.files?.[0] || "";
                formik.setFieldValue("document", file);
                setPreview(file ? URL.createObjectURL(file) : "");
              }}
            />

            {preview && (
              <img
                src={preview}
                alt="Preview"
                className="object-contain w-20 h-20 mt-2 border rounded"
              />
            )}
          </div>
        </div>

        <div className="border-b" />

        <div className="flex justify-end gap-2 p-4">
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            loadingText="Updating..."
          >
            Update
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditVendorCompliance;
